import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Calendar, MapPin, Users, Crown, Clock, Ticket, ChevronRight, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import EventsComingSoon from '../components/EventsComingSoon';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Events = () => {
  const { user, token } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tierFilter, setTierFilter] = useState('');

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events?upcoming=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (eventId) => {
    try {
      await axios.post(`${API}/events/${eventId}/register`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Successfully registered for event!');
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register');
    }
  };

  const getTierBadge = (tier) => {
    const styles = {
      elite: 'bg-gradient-to-r from-[#c8b273] to-[#96834d] text-black',
      pro: 'bg-[#ff5e00] text-white',
      basic: 'bg-[#2d352d] text-[#6b7d6b]'
    };
    return styles[tier] || styles.basic;
  };

  const canRegister = (requiredTier) => {
    const tiers = ['basic', 'pro', 'elite'];
    const userTierIdx = tiers.indexOf(user?.subscription_tier || 'basic');
    const requiredTierIdx = tiers.indexOf(requiredTier);
    return userTierIdx >= requiredTierIdx;
  };

  const filteredEvents = tierFilter && tierFilter !== 'all'
    ? events.filter(e => e.tier_required === tierFilter)
    : events;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return {
      day: date.getDate(),
      month: date.toLocaleString('default', { month: 'short' }),
      year: date.getFullYear(),
      time: date.toLocaleTimeString('default', { hour: '2-digit', minute: '2-digit' })
    };
  };

  return (
    <div className="min-h-screen" data-testid="events-page">
      {/* Hero */}
      <div 
        className="relative h-64 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1758272960374-343f5eb7fdcd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxmcmllbmRzJTIwY2FtcGluZyUyMGJ5JTIwbGFrZSUyMG5pZ2h0JTIwZmlyZXxlbnwwfHx8fDE3NzExNjg0MTh8MA&ixlib=rb-4.1.0&q=85')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-[#050605] via-[#050605]/70 to-transparent" />
        <div className="relative z-10 h-full flex items-end pb-8 px-6 md:px-8">
          <div className="max-w-7xl mx-auto w-full">
            <p className="data-label text-[#c8b273] mb-2">Competitions & Meetups</p>
            <h1 className="font-['Barlow_Condensed'] text-4xl font-extrabold uppercase tracking-wider text-white">
              Events Calendar
            </h1>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="sticky top-16 z-20 bg-[#050605]/95 backdrop-blur-sm border-b border-white/5 py-4 px-6 md:px-8">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <p className="text-[#8a948a]">{filteredEvents.length} upcoming events</p>
          <Select value={tierFilter} onValueChange={setTierFilter}>
            <SelectTrigger className="w-48 bg-[#0b0d0b] border-white/10 text-white" data-testid="tier-filter">
              <Crown className="w-4 h-4 mr-2" />
              <SelectValue placeholder="All Tiers" />
            </SelectTrigger>
            <SelectContent className="bg-[#0b0d0b] border-white/10">
              <SelectItem value="all">All Tiers</SelectItem>
              <SelectItem value="basic">Basic</SelectItem>
              <SelectItem value="pro">Pro</SelectItem>
              <SelectItem value="elite">Elite</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Events List */}
      <div className="p-6 md:p-8">
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="glass-card h-40 animate-pulse bg-[#141714]" />
              ))}
            </div>
          ) : filteredEvents.length === 0 ? (
            // No events scheduled → premium "Coming Soon" lock state. Once
            // events are added (and the upcoming filter passes), this branch
            // is bypassed automatically and live cards render normally.
            // When a tier filter is active and just produces zero results, we
            // keep the lightweight inline message so users know to clear it.
            tierFilter && tierFilter !== 'all' ? (
              <div className="glass-card p-12 text-center" data-testid="events-empty-filter">
                <Calendar className="w-16 h-16 text-[#4a504a] mx-auto mb-4" />
                <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-white mb-2">
                  No matching events
                </h3>
                <p className="text-[#8a948a]">Try clearing the tier filter.</p>
              </div>
            ) : (
              <EventsComingSoon />
            )
          ) : (
            <div className="space-y-4">
              {filteredEvents.map((event, index) => {
                const date = formatDate(event.event_date);
                const canJoin = canRegister(event.tier_required);
                const spotsLeft = event.max_participants ? event.max_participants - event.current_participants : null;
                
                return (
                  <motion.div
                    key={event.id}
                    className="glass-card overflow-hidden"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    data-testid={`event-card-${event.id}`}
                  >
                    <div className="flex flex-col md:flex-row">
                      {/* Date Badge */}
                      <div className="md:w-32 bg-[#141714] p-4 flex md:flex-col items-center justify-center text-center border-b md:border-b-0 md:border-r border-white/5">
                        <p className="font-['Barlow_Condensed'] text-4xl font-bold text-[#c8b273]">{date.day}</p>
                        <p className="text-white uppercase tracking-wider ml-2 md:ml-0">{date.month}</p>
                      </div>

                      {/* Content */}
                      <div className="flex-1 p-4 md:p-6">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded ${getTierBadge(event.tier_required)}`}>
                                {event.tier_required}
                              </span>
                              {event.entry_fee && (
                                <span className="text-xs text-[#c8b273] font-bold">
                                  £{event.entry_fee} Entry
                                </span>
                              )}
                            </div>
                            <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wide text-white">
                              {event.title}
                            </h3>
                            <p className="text-[#8a948a] mt-2 line-clamp-2">{event.description}</p>
                            
                            <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-[#8a948a]">
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {event.location}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {date.time}
                              </span>
                              {spotsLeft !== null && (
                                <span className="flex items-center gap-1">
                                  <Users className="w-4 h-4" />
                                  {spotsLeft > 0 ? `${spotsLeft} spots left` : 'Fully booked'}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Action */}
                          <div className="hidden md:flex flex-col items-end gap-2">
                            {canJoin ? (
                              <Button 
                                className="btn-gold"
                                onClick={() => handleRegister(event.id)}
                                disabled={spotsLeft === 0}
                                data-testid={`register-btn-${event.id}`}
                              >
                                {spotsLeft === 0 ? 'Full' : 'Register'}
                                <ChevronRight className="w-4 h-4 ml-1" />
                              </Button>
                            ) : (
                              <div className="text-right">
                                <p className="text-[#ff5e00] text-sm font-bold uppercase">
                                  Upgrade Required
                                </p>
                                <Link to="/subscription">
                                  <Button variant="ghost" className="text-[#c8b273] text-sm p-0 mt-1">
                                    Upgrade to {event.tier_required}
                                  </Button>
                                </Link>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Mobile Action */}
                        <div className="md:hidden mt-4 pt-4 border-t border-white/5">
                          {canJoin ? (
                            <Button 
                              className="btn-gold w-full"
                              onClick={() => handleRegister(event.id)}
                              disabled={spotsLeft === 0}
                            >
                              {spotsLeft === 0 ? 'Fully Booked' : 'Register Now'}
                            </Button>
                          ) : (
                            <Link to="/subscription" className="block">
                              <Button className="btn-ghost w-full">
                                Upgrade to {event.tier_required}
                              </Button>
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Events;
